import { CopyIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import useSWR from 'swr'

import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceDiv,
  PreferenceInfo,
  PreferenceInput,
  PreferenceScreen,
  PreferenceSubmit,
} from '../../components/Preference'
import { QrSvg } from '../../components/QrSvg'
import { fetcher } from '../../lib/swr'
import { type InviteRow, inviteStatus, toDatetimeLocalValue } from './invites'

export default function InviteDetailSettings() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const invites = useSWR<InviteRow[]>('/api/invites', fetcher)
  const invite = invites.data?.find((i) => String(i.id) === id) ?? null

  const [draftNote, setDraftNote] = useState('')
  const [draftExpires, setDraftExpires] = useState('')
  const [draftMaxClaims, setDraftMaxClaims] = useState(1)
  const [busy, setBusy] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (invites.error) {
      toast.error(invites.error.message || 'Kon uitnodiging niet laden')
    }
  }, [invites.error])

  useEffect(() => {
    if (!invite) return
    setDraftNote(invite.note ?? '')
    setDraftExpires(toDatetimeLocalValue(invite.expiresAt))
    setDraftMaxClaims(invite.maxClaims)
    setReady(true)
  }, [invite])

  const dirty =
    invite &&
    ready &&
    ((invite.note ?? '') !== draftNote.trim() ||
      toDatetimeLocalValue(invite.expiresAt) !== draftExpires ||
      invite.maxClaims !== draftMaxClaims)

  async function saveInvite() {
    if (!invite) return
    setBusy(true)
    try {
      const res = await fetch(`/api/invites/${invite.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note: draftNote.trim() || null,
          expiresAt: new Date(draftExpires).toISOString(),
          maxClaims: draftMaxClaims,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.message || 'Bewaren mislukt')
        return
      }
      toast.success('Bewaard')
      await invites.mutate()
    } finally {
      setBusy(false)
    }
  }

  async function revokeInvite() {
    if (!invite) return
    setBusy(true)
    try {
      const res = await fetch(`/api/invites/${invite.id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.message || 'Intrekken mislukt')
        return
      }
      toast.success('Uitnodiging ingetrokken')
      await invites.mutate()
      navigate('/settings/invites', { replace: true })
    } finally {
      setBusy(false)
    }
  }

  if (invites.isLoading && !invite) {
    return (
      <PreferenceScreen title="Uitnodiging" backHref="/settings/invites">
        <PreferenceInfo title="Laden…" />
      </PreferenceScreen>
    )
  }

  if (!invite) {
    return (
      <PreferenceScreen title="Uitnodiging" backHref="/settings/invites">
        <PreferenceCategory filled>
          <PreferenceInfo
            title="Niet gevonden"
            summary="Deze uitnodiging bestaat niet (meer)."
          />
        </PreferenceCategory>
      </PreferenceScreen>
    )
  }

  return (
    <PreferenceScreen title={invite.code} backHref="/settings/invites">
      <PreferenceCategory filled>
        <PreferenceDiv className="flex flex-col items-center gap-3 px-5 py-6 bg-white/6 first:rounded-t-2xl last:rounded-b-2xl rounded">
          <QrSvg value={invite.url} size={180} />
        </PreferenceDiv>
        <PreferenceButton
          icon={<CopyIcon className="h-6 w-6" />}
          title="Kopieer code"
          summary={invite.code}
          onPress={() => {
            void navigator.clipboard.writeText(invite.code)
            toast.success('Code gekopieerd')
          }}
        />
        <PreferenceButton
          icon={<CopyIcon className="h-6 w-6" />}
          title="Kopieer link"
          summary={invite.url}
          onPress={() => {
            void navigator.clipboard.writeText(invite.url)
            toast.success('Link gekopieerd')
          }}
        />
      </PreferenceCategory>

      <PreferenceCategory title="Bewerken" filled>
        <PreferenceInput
          title="Notitie"
          value={draftNote}
          onChange={setDraftNote}
          placeholder="bijv. voor oma"
        />
        <PreferenceInput
          title="Geldig tot"
          type="datetime-local"
          value={draftExpires}
          onChange={setDraftExpires}
        />
        <PreferenceInput
          title="Max. claims"
          type="number"
          min={1}
          value={String(draftMaxClaims)}
          onChange={(v) => setDraftMaxClaims(Math.max(1, Number(v) || 1))}
        />
        <PreferenceSubmit
          title="Bewaren"
          onPress={saveInvite}
          disabled={busy || !dirty}
        />
      </PreferenceCategory>

      {!invite.revokedAt ? (
        <PreferenceCategory filled>
          <PreferenceButton
            title="Trek uitnodiging in"
            onPress={revokeInvite}
            disabled={busy}
            isDestructive
          />
        </PreferenceCategory>
      ) : null}
    </PreferenceScreen>
  )
}
